// Per-camera figures at the instant the readout row is describing - the breakdown behind the
// Section-wide figures productivityReadout resolves for that same instant.
//
// Its own module rather than more fields on ProductivityReadout: the Section readout resolves a cursor
// position against buckets of two grains, while a camera's figures are read at the instant that lookup
// already settled on, so nothing here needs the cursor, the granularity or the metric registry.

import type { CameraSeries, WeightSeriesDay } from '@/types/SectionLiveKpi';
import { cameraColor, cameraLabel, cameraOrdinal } from './cameraSeries';
import { bucketTimestamp } from './productivityFormatting';
import { productivityPlottableValue } from './productivitySeries';

/**
 * One camera as the readout row states it: what to call it, the colour its curves are drawn in, and the
 * three figures it stood at.
 *
 * The three are keyed by metric key rather than bucket field, so a caller walking the camera metrics can
 * read the matching figure with `entry[key]` - the same shape METRIC_FIGURE_FIELD gives the Section's row.
 *
 * `ordinal` is 1-based and is the camera's drawing identity: its colour, its series ids, and the URL
 * parameter that isolates it. See cameraSeriesKey for why the ordinal and not the `sensor_id`.
 */
export type CameraReadoutEntry = {
    ordinal: number;
    label: string;
    color: string;
    // Which device this is, for anything that has to survive a change of window - see
    // resolveHiddenCameras. The ordinal above is a drawing position and nothing more.
    sensorId: number;
    weight: number | null;
    dailyGain: number | null;
    growthIndex: number | null;
};

/**
 * One camera's buckets by instant, built once per bucket array rather than scanned per lookup. The cursor
 * re-resolves this readout at every bucket it crosses - thousands per camera at hour granularity over a
 * long range. Keyed on the array itself, so a new response rebuilds and a cursor move does not.
 */
const bucketIndexCache = new WeakMap<readonly WeightSeriesDay[], Map<number, WeightSeriesDay>>();

function bucketsByTimestamp(buckets: readonly WeightSeriesDay[]): Map<number, WeightSeriesDay> {
    const cached = bucketIndexCache.get(buckets);
    if (cached !== undefined) return cached;

    const index = new Map(buckets.map((bucket) => [bucketTimestamp(bucket), bucket]));
    bucketIndexCache.set(buckets, index);
    return index;
}

/**
 * Every camera of the Section, with what it stated at `timestampMs` - including one that stated nothing
 * there, carrying three nulls. The entries are a roster the reader reads ordinal by ordinal against the
 * curves on the plot, so a camera dropping out at some cursor positions would renumber the ones after it.
 */
export function cameraReadoutAt(cameras: readonly CameraSeries<WeightSeriesDay>[], timestampMs: number | null): CameraReadoutEntry[] {
    return cameras.map((camera, index) => {
        // The exact instant, not the nearest. A camera bucket shares its date/hour key with the aggregate
        // bucket the readout resolved against, so a miss means the camera stated nothing there - and
        // answering that with a neighbouring reading erases what a reader breaking down a withheld
        // aggregate is looking for.
        const bucket = timestampMs === null ? undefined : bucketsByTimestamp(camera.buckets).get(timestampMs);
        return {
            ordinal: cameraOrdinal(index),
            label: cameraLabel(camera, index),
            color: cameraColor(index),
            sensorId: camera.sensor_id,
            weight: bucket?.weight_kg ?? null,
            // Filtered exactly as the Section's own row filters them: a Growth Index of -201 is not a
            // reading to repeat here just because the chart declined to plot it. See readoutAt.
            dailyGain: productivityPlottableValue('dailyGain', bucket?.daily_gain_g ?? null),
            growthIndex: productivityPlottableValue('growthIndex', bucket?.growth_index ?? null),
        };
    });
}
